import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@reflex/ui/components/ui/dialog';
import { KbdCombo } from '@reflex/ui/components/shared/Kbd';
import { SectionLabel } from '@reflex/ui/components/layout/Typography';
import { useHotkey } from '@/hooks/ui/useHotkey';
import { shortcutReference } from '@/lib/hotkeys';

export interface HotkeysDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * The keyboard shortcut listing, reachable from ⌘K ("Keyboard Shortcuts") and
 * from `⌥⌘/`.
 *
 * The global group is generated from the `HOTKEYS` table rather than written
 * out here, so a shortcut cannot exist without being listed, and the listing
 * cannot claim a chord the app no longer answers to. Component-owned
 * shortcuts (view tabs, turn stepping) come from the same module's
 * display-only list, so this dialog is the whole keyboard surface.
 *
 * Its own binding lives here, next to the surface it opens, matching the ⌘K
 * and jump-palette rule.
 */
export function HotkeysDialog({ open, onOpenChange }: HotkeysDialogProps) {
  useHotkey('viewHotkeys', () => onOpenChange(true));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Keyboard shortcuts</DialogTitle>
          <DialogDescription>⌥ is the Alt key on Windows and Linux.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-5">
          {shortcutReference().map((group) => (
            <div key={group.title} className="flex flex-col gap-1">
              <SectionLabel as="h3">{group.title}</SectionLabel>
              <ul className="flex flex-col">
                {group.items.map((item) => (
                  <li
                    key={`${group.title}:${item.label}`}
                    className="flex items-center justify-between gap-4 border-b border-border/40 py-1.5 last:border-b-0"
                  >
                    <span className="text-sm text-foreground">{item.label}</span>
                    <KbdCombo keys={item.keys} size="sm" />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
